/**
 * Build a timed GPS track for Vehicle Map playback from delivery stops + route segments.
 */

export interface PlaybackTrackPoint {
  /** Unix ms */
  t: number;
  lat: number;
  lon: number;
}

export interface PlaybackEventMarker {
  id: string;
  t: number;
  label: string;
  kind: 'start' | 'stop' | 'delivery' | 'handoff' | 'end';
}

export interface VehicleDayPlaybackTrack {
  points: PlaybackTrackPoint[];
  events: PlaybackEventMarker[];
  fromIso: string;
  toIso: string;
}

type StopLike = {
  stop_number: number;
  arrival_time?: string;
  departure_time?: string;
  latitude?: number | null;
  longitude?: number | null;
  stop_type?: string;
  postcode?: string;
};

type SegmentLike = {
  from_stop_number: number;
  to_stop_number: number;
  segment_start?: string;
  segment_end?: string;
  points?: [number, number][];
};

/**
 * GPS / telematics instants are UTC. Trino and some CSV/JSONL paths emit
 * timezone-less strings (`2026-09-15 08:47:29` or `2026-09-15T08:47:29`).
 * Browsers parse those as *local* time, so Europe/London formatting cannot
 * apply BST and the replay clock looks stuck on UTC wall time.
 *
 * Strings that already carry `Z` or an offset are left alone.
 */
export function normalizeUtcTimestamp(raw: string): string {
  const s = raw.trim();
  if (!s) return s;
  if (/[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}(?::\d{2})?$/.test(s)) {
    return s.includes(' ') && !s.includes('T') ? s.replace(' ', 'T') : s;
  }
  const withT = s.includes('T') ? s : s.replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T');
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(withT)) {
    return `${withT}Z`;
  }
  return s;
}

function parseMs(iso: string | undefined | null): number | null {
  if (!iso) return null;
  const t = Date.parse(normalizeUtcTimestamp(iso));
  return Number.isFinite(t) ? t : null;
}

/** Clock for Vehicle Map replay — always Europe/London (BST/GMT). */
export function formatUkPlaybackClock(iso: string | null | undefined): string {
  if (!iso) return '—:—:—';
  const ms = parseMs(iso);
  if (ms == null) return '—:—:—';
  return new Date(ms).toLocaleTimeString('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** Calendar date for Vehicle Map replay — YYYY-MM-DD in Europe/London. */
export function formatUkPlaybackDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = parseMs(iso);
  if (ms == null) return '—';
  // en-CA yields YYYY-MM-DD
  return new Date(ms).toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
}

function validCoord(lat: number | null | undefined, lon: number | null | undefined): boolean {
  return (
    lat != null
    && lon != null
    && Number.isFinite(lat)
    && Number.isFinite(lon)
    && lat !== 0
    && lon !== 0
  );
}

function interpolateSegmentPoints(
  pts: [number, number][],
  startMs: number,
  endMs: number,
): PlaybackTrackPoint[] {
  const valid = pts.filter(([lat, lon]) => validCoord(lat, lon));
  if (valid.length === 0) return [];
  if (valid.length === 1) {
    return [{ t: startMs, lat: valid[0][0], lon: valid[0][1] }];
  }
  const span = Math.max(1, endMs - startMs);
  return valid.map(([lat, lon], i) => ({
    t: startMs + (span * i) / (valid.length - 1),
    lat,
    lon,
  }));
}

function eventKind(stopType: string | undefined): PlaybackEventMarker['kind'] {
  const t = (stopType || '').toLowerCase();
  if (t.includes('day start') || t === 'start') return 'start';
  if (t.includes('day end') || t === 'end') return 'end';
  if (t.includes('delivery')) return 'delivery';
  return 'stop';
}

function eventLabel(stop: StopLike): string {
  const kind = eventKind(stop.stop_type);
  const place = (stop.postcode || '').trim();
  if (kind === 'start') return place ? `Route start · ${place}` : 'Route start';
  if (kind === 'end') return place ? `Route end · ${place}` : 'Route end';
  if (kind === 'delivery') {
    return place ? `Delivery · ${place}` : `Delivery stop ${stop.stop_number}`;
  }
  return place ? `Stop ${stop.stop_number} · ${place}` : `Stop ${stop.stop_number}`;
}

/**
 * Prefer segment GPS with timestamps; fall back to stop arrivals.
 */
export function buildVehicleDayPlaybackTrack(
  stops: StopLike[],
  segments: SegmentLike[],
): VehicleDayPlaybackTrack | null {
  const sortedStops = [...stops].sort((a, b) => a.stop_number - b.stop_number);
  const sortedSegs = [...segments].sort(
    (a, b) => a.from_stop_number - b.from_stop_number || a.to_stop_number - b.to_stop_number,
  );

  const stopByNumber = new Map(sortedStops.map((s) => [s.stop_number, s]));
  const points: PlaybackTrackPoint[] = [];

  for (const seg of sortedSegs) {
    const rawPts = seg.points ?? [];
    if (rawPts.length === 0) continue;

    let startMs = parseMs(seg.segment_start);
    let endMs = parseMs(seg.segment_end);

    const fromStop = stopByNumber.get(seg.from_stop_number);
    const toStop = stopByNumber.get(seg.to_stop_number);
    if (startMs == null) startMs = parseMs(fromStop?.departure_time) ?? parseMs(fromStop?.arrival_time);
    if (endMs == null) endMs = parseMs(toStop?.arrival_time) ?? parseMs(toStop?.departure_time);

    if (startMs == null || endMs == null) continue;
    if (endMs < startMs) endMs = startMs + 60_000;

    const interpolated = interpolateSegmentPoints(rawPts, startMs, endMs);
    // Avoid duplicating the join point when chaining segments
    if (points.length > 0 && interpolated.length > 0) {
      points.push(...interpolated.slice(1));
    } else {
      points.push(...interpolated);
    }
  }

  if (points.length < 2) {
    for (const stop of sortedStops) {
      if (!validCoord(stop.latitude, stop.longitude)) continue;
      const t = parseMs(stop.arrival_time) ?? parseMs(stop.departure_time);
      if (t == null) continue;
      points.push({ t, lat: stop.latitude!, lon: stop.longitude! });
    }
  }

  if (points.length < 2) return null;

  points.sort((a, b) => a.t - b.t);

  const events: PlaybackEventMarker[] = [];
  for (const stop of sortedStops) {
    const t = parseMs(stop.arrival_time) ?? parseMs(stop.departure_time);
    if (t == null) continue;
    events.push({
      id: `stop-${stop.stop_number}-${t}`,
      t,
      label: eventLabel(stop),
      kind: eventKind(stop.stop_type),
    });
  }
  events.sort((a, b) => a.t - b.t);

  const fromMs = points[0].t;
  const toMs = points[points.length - 1].t;
  return {
    points,
    events,
    fromIso: new Date(fromMs).toISOString(),
    toIso: new Date(toMs).toISOString(),
  };
}

type OrderJourneySegmentLike = SegmentLike & {
  label?: string | null;
  vehicle?: string | null;
  coordinates?: [number, number][];
};

function legSortKey(label: string | null | undefined): number {
  const l = String(label ?? '').toLowerCase();
  if (l.includes('trunk')) return 0;
  if (l.includes('delivery')) return 1;
  return 2;
}

function segmentPointsForPlayback(seg: OrderJourneySegmentLike): [number, number][] {
  if (Array.isArray(seg.points) && seg.points.length >= 2) {
    return seg.points.filter(([lat, lon]) => validCoord(lat, lon));
  }
  // Synthetic order legs store MapLibre [lon, lat]
  if (Array.isArray(seg.coordinates) && seg.coordinates.length >= 2) {
    return seg.coordinates
      .filter(([lon, lat]) => validCoord(lat, lon))
      .map(([lon, lat]) => [lat, lon] as [number, number]);
  }
  return [];
}

/**
 * Multi-leg order replay: play Trunk then Delivery in journey order.
 * Does NOT merge-sort two vehicles' wall-clock GPS (that makes the marker
 * jump between plates and can place "delivery" at a late return-to-hub).
 */
export function buildOrderJourneyPlaybackTrack(
  segments: OrderJourneySegmentLike[],
): VehicleDayPlaybackTrack | null {
  if (!Array.isArray(segments) || segments.length === 0) return null;

  const legs = [...segments].sort((a, b) => {
    const ka = legSortKey(a.label);
    const kb = legSortKey(b.label);
    if (ka !== kb) return ka - kb;
    return (a.from_stop_number ?? 0) - (b.from_stop_number ?? 0);
  });

  const points: PlaybackTrackPoint[] = [];
  let cursorMs: number | null = null;

  for (const seg of legs) {
    const rawPts = segmentPointsForPlayback(seg);
    if (rawPts.length < 2) continue;

    let startMs = parseMs(seg.segment_start);
    let endMs = parseMs(seg.segment_end);

    // Missing times: estimate ~2 min/km from polyline length (order-map synthetic legs).
    if (startMs == null || endMs == null) {
      let metres = 0;
      for (let i = 1; i < rawPts.length; i += 1) {
        const a = rawPts[i - 1]!;
        const b = rawPts[i]!;
        metres += haversineMetres(a[0], a[1], b[0], b[1]);
      }
      const durationMs = Math.max(60_000, Math.round((metres / 1000) * 2 * 60_000));
      startMs = cursorMs != null ? cursorMs + 1_000 : Date.UTC(1970, 0, 1, 6, 0, 0);
      endMs = startMs + durationMs;
    }

    // Keep journey monotonic: delivery must not jump backward into trunk wall-clock.
    if (cursorMs != null && startMs < cursorMs) {
      const duration = Math.max(60_000, endMs - startMs);
      startMs = cursorMs + 1_000;
      endMs = startMs + duration;
    }
    if (endMs < startMs) endMs = startMs + 60_000;

    const interpolated = interpolateSegmentPoints(rawPts, startMs, endMs);
    if (points.length > 0 && interpolated.length > 0) {
      points.push(...interpolated.slice(1));
    } else {
      points.push(...interpolated);
    }
    cursorMs = endMs;
  }

  if (points.length < 2) return null;

  // Journey order only — do not re-sort by wall clock (would re-interleave vehicles).
  return {
    points,
    events: [],
    fromIso: new Date(points[0]!.t).toISOString(),
    toIso: new Date(points[points.length - 1]!.t).toISOString(),
  };
}

/** Lat/lon at (or interpolated to) timestamp along the track. */
export function positionAtTimestamp(
  points: PlaybackTrackPoint[],
  timestampIso: string | null,
): { lat: number; lon: number } | null {
  if (!points.length || !timestampIso) return null;
  const t = parseMs(timestampIso);
  if (t == null) return null;
  if (t <= points[0].t) return { lat: points[0].lat, lon: points[0].lon };
  if (t >= points[points.length - 1].t) {
    const last = points[points.length - 1];
    return { lat: last.lat, lon: last.lon };
  }
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (t >= a.t && t <= b.t) {
      const span = Math.max(1, b.t - a.t);
      const u = (t - a.t) / span;
      return {
        lat: a.lat + (b.lat - a.lat) * u,
        lon: a.lon + (b.lon - a.lon) * u,
      };
    }
  }
  return { lat: points[0].lat, lon: points[0].lon };
}

/** Approx metres between two WGS84 points (stop↔order / GPS↔delivery matching). */
export function haversineMetres(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** First GPS sample within this radius counts as "near delivery/handoff". */
export const ORDER_DELIVERY_PROXIMITY_M = 400;

export type OrderDeliveryProximityInput = {
  orderId: string;
  lat: number;
  lon: number;
  postcode?: string;
  city?: string;
};

export type OrderHandoffProximityInput = {
  orderId: string;
  lat: number;
  lon: number;
  /** e.g. "Depot 102" */
  label?: string;
  postcode?: string;
};

type ProximityTarget = {
  orderId: string;
  lat: number;
  lon: number;
  id: string;
  kind: 'delivery' | 'handoff';
  label: string;
};

function findFirstApproachEvents(
  points: PlaybackTrackPoint[],
  targets: ProximityTarget[],
  maxMetres: number,
): PlaybackEventMarker[] {
  if (points.length === 0 || targets.length === 0) return [];
  const events: PlaybackEventMarker[] = [];
  for (const target of targets) {
    if (!validCoord(target.lat, target.lon) || !target.orderId) continue;
    let hit: PlaybackTrackPoint | null = null;
    for (const p of points) {
      if (haversineMetres(p.lat, p.lon, target.lat, target.lon) <= maxMetres) {
        hit = p;
        break;
      }
    }
    if (!hit) continue;
    events.push({
      id: target.id,
      t: hit.t,
      label: target.label,
      kind: target.kind,
    });
  }
  events.sort((a, b) => a.t - b.t);
  return events;
}

/**
 * Amber scrubber marks: first time the GPS track enters `maxMetres` of each
 * order delivery pin. Returns [] if the vehicle never gets that close.
 */
export function findOrderDeliveryProximityEvents(
  points: PlaybackTrackPoint[],
  deliveries: OrderDeliveryProximityInput[],
  maxMetres: number = ORDER_DELIVERY_PROXIMITY_M,
): PlaybackEventMarker[] {
  return findFirstApproachEvents(
    points,
    deliveries.map((dest) => {
      const pc = (dest.postcode ?? '').trim();
      const cityRaw = (dest.city ?? '').trim();
      const city = cityRaw.toLowerCase() === 'uk' ? 'UK' : cityRaw;
      const place = [pc, city].filter(Boolean).join(' · ');
      return {
        orderId: dest.orderId,
        lat: dest.lat,
        lon: dest.lon,
        id: `order-delivery-proximity-${dest.orderId}`,
        kind: 'delivery' as const,
        label: place ? `Delivery · ${place}` : `Delivery · ${dest.orderId}`,
      };
    }),
    maxMetres,
  );
}

/**
 * Cyan scrubber marks: first GPS approach to a pick-up/trunk hub handoff pin.
 */
export function findOrderHandoffProximityEvents(
  points: PlaybackTrackPoint[],
  handoffs: OrderHandoffProximityInput[],
  maxMetres: number = ORDER_DELIVERY_PROXIMITY_M,
): PlaybackEventMarker[] {
  return findFirstApproachEvents(
    points,
    handoffs.map((hub) => {
      const label = (hub.label ?? '').trim()
        || (hub.postcode ?? '').trim()
        || hub.orderId;
      return {
        orderId: hub.orderId,
        lat: hub.lat,
        lon: hub.lon,
        id: `order-handoff-proximity-${hub.orderId}`,
        kind: 'handoff' as const,
        label: `Handoff · ${label}`,
      };
    }),
    maxMetres,
  );
}

/** Merge delivery + handoff proximity marks into a day track (sorted by time). */
export function withOrderProximityEvents(
  track: VehicleDayPlaybackTrack,
  opts: {
    deliveries?: OrderDeliveryProximityInput[];
    handoffs?: OrderHandoffProximityInput[];
    maxMetres?: number;
  },
): VehicleDayPlaybackTrack {
  const maxMetres = opts.maxMetres ?? ORDER_DELIVERY_PROXIMITY_M;
  const added = [
    ...findOrderDeliveryProximityEvents(track.points, opts.deliveries ?? [], maxMetres),
    ...findOrderHandoffProximityEvents(track.points, opts.handoffs ?? [], maxMetres),
  ];
  if (added.length === 0) return track;
  const existingIds = new Set(track.events.map((e) => e.id));
  const merged = [
    ...track.events,
    ...added.filter((e) => !existingIds.has(e.id)),
  ].sort((a, b) => a.t - b.t);
  return { ...track, events: merged };
}

/** @deprecated Prefer withOrderProximityEvents */
export function withOrderDeliveryProximityEvents(
  track: VehicleDayPlaybackTrack,
  deliveries: OrderDeliveryProximityInput[],
  maxMetres: number = ORDER_DELIVERY_PROXIMITY_M,
): VehicleDayPlaybackTrack {
  return withOrderProximityEvents(track, { deliveries, maxMetres });
}

/**
 * Progressive trail from start through `timestampIso` as MapLibre [lon, lat] pairs.
 * Includes the interpolated head position so the line meets the playback marker.
 */
export function trailCoordinatesUpToTimestamp(
  points: PlaybackTrackPoint[],
  timestampIso: string | null,
): [number, number][] {
  if (!points.length || !timestampIso) return [];
  const t = parseMs(timestampIso);
  if (t == null) return [];

  const trail: [number, number][] = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (p.t <= t) {
      trail.push([p.lon, p.lat]);
      continue;
    }
    if (i > 0) {
      const a = points[i - 1];
      const span = Math.max(1, p.t - a.t);
      const u = (t - a.t) / span;
      trail.push([
        a.lon + (p.lon - a.lon) * u,
        a.lat + (p.lat - a.lat) * u,
      ]);
    }
    break;
  }

  // Drop consecutive duplicate vertices
  const deduped: [number, number][] = [];
  for (const c of trail) {
    const prev = deduped[deduped.length - 1];
    if (!prev || prev[0] !== c[0] || prev[1] !== c[1]) deduped.push(c);
  }
  return deduped;
}
